import React, { useState, useEffect } from 'react';
import Modal from '../shared/Modal';
import { useGovernance } from '../../context/GovernanceContext';

const EMPTY = { name: '', industry: '', contact_name: '', contact_email: '' };

export default function ClientForm({ isOpen, onClose, client }) {
  const { addClient, updateClient } = useGovernance();
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const isEdit = Boolean(client);

  useEffect(() => {
    if (isOpen) {
      setForm(
        client
          ? {
              name: client.name || '',
              industry: client.industry || '',
              contact_name: client.contact_name || '',
              contact_email: client.contact_email || '',
            }
          : EMPTY
      );
      setError('');
    }
  }, [isOpen, client]);

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { setError('Client name is required.'); return; }
    setSaving(true);
    setError('');
    try {
      if (isEdit) {
        await updateClient(client.id, form);
      } else {
        await addClient(form);
      }
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEdit ? 'Edit Client' : 'Add Client'}
      size="lg"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-xs font-semibold text-brand-navy mb-1">
            Client name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            required
            value={form.name}
            onChange={set('name')}
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-brand-navy"
            placeholder="Acme Corporation"
          />
        </div>

        <div>
          <label className="block text-xs font-semibold text-brand-navy mb-1">Industry</label>
          <input
            type="text"
            value={form.industry}
            onChange={set('industry')}
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-brand-navy"
            placeholder="Financial Services"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-semibold text-brand-navy mb-1">Contact name</label>
            <input
              type="text"
              value={form.contact_name}
              onChange={set('contact_name')}
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-brand-navy"
              placeholder="Jane Smith"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-brand-navy mb-1">Contact email</label>
            <input
              type="email"
              value={form.contact_email}
              onChange={set('contact_email')}
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-brand-navy"
              placeholder="jane@acme.com"
            />
          </div>
        </div>

        {error && (
          <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">
            {error}
          </div>
        )}

        <div className="flex justify-end gap-3 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded border border-gray-300 text-gray-700 hover:bg-gray-50 text-sm font-medium transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="px-4 py-2 rounded bg-brand-navy text-white text-sm font-semibold hover:opacity-90 disabled:opacity-50 transition-opacity"
          >
            {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Add client'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
